import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { catchError, filter, map, take, tap } from 'rxjs/operators'
import { UrlCollection } from '../url-collection'
import { AuthService } from '../auth/services/auth.service'
import { Router } from '@angular/router'
import { EntityLinkService } from '../modules/app-menu/services/entity-link.service'
import { NavSupportService } from '../modules/app-menu'

export interface Favorite {
  resolvedURL$?: Observable<string>
  id: string
  name: string
  icon: string
  route: string
  dtoType?: string
  order?: number
}

@Injectable({
  providedIn: 'root'
})
export class FavoritesService {
  private readonly STORAGE_KEY = 'GUI_FAVORITES'
  private favoritesSubject = new BehaviorSubject<Favorite[]>([])

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private entityLinkService: EntityLinkService,
    private router: Router
  ) {
    this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      if (!isLoggedIn) {
        this.resetFavorites()
      } else {
        this.loadFavorites()
      }
    })
  }

  // actual product of this piece of code
  get favorites$(): Observable<Favorite[]> {
    return this.favoritesSubject.asObservable()
  }

  isFavorite(id: string): boolean {
    return this.favoritesSubject.value.some((f) => f.id === id)
  }

  toggleFavorite(favorite: Favorite): void {
    const currentFavorites = this.favoritesSubject.value
    const existingIndex = currentFavorites.findIndex((f) => f.id === favorite.id)

    if (existingIndex >= 0) {
      // remove, if already existing
      const newFavorites = currentFavorites.filter((f) => f.id !== favorite.id)
      this.favoritesSubject.next(newFavorites)
      this.saveFavorites(newFavorites)
    } else {
      // add, if not already existing
      const newFavorites = [...currentFavorites, favorite]
      this.favoritesSubject.next(newFavorites)
      this.saveFavorites(newFavorites)
    }
  }

  loadFavorites(): void {
    this.http
      .get<any>(UrlCollection.Userstore.GET(this.STORAGE_KEY))
      .pipe(
        catchError((err) => {
          return of({ favorites: [] })
        })
      )
      .subscribe((data) => {
        const favorites = data?.favorites || []
        this.favoritesSubject.next(favorites)
      })
  }

  private saveFavorites(favorites: Favorite[]): void {
    const payload = { favorites: favorites }

    this.http
      .post(UrlCollection.Userstore.SAVE(this.STORAGE_KEY), payload)
      .pipe(
        catchError((err) => {
          return of(null)
        })
      )
      .subscribe()
  }

  reorderFavorites(favorites: Favorite[]): void {
    this.favoritesSubject.next(favorites)
    this.saveFavorites(favorites)
  }

  removeFavorite(id: string): void {
    const newFavorites = this.favoritesSubject.value.filter((f) => f.id !== id)
    this.favoritesSubject.next(newFavorites)
    this.saveFavorites(newFavorites)
  }

  removeAllFavorites(): void {
    this.favoritesSubject.next([])
    this.saveFavorites([])
  }

  resetFavorites(): void {
    this.favoritesSubject.next([])
  }

  navigateToFavorite(favorite: Favorite): void {
    if (!favorite || !favorite.route) return

    // check, if it's a specific entity link
    if (favorite.route.startsWith('/entity-link/') && favorite.dtoType) {
      // format is "/entity-link/{dtoType}"
      const entityType = favorite.dtoType

      this.entityLinkService
        .getEntityLinkData$({
          type: entityType
          // no id since we want to navigate to the overview page
        })
        .pipe(take(1))
        .subscribe((result) => {
          this.router.navigate([result.url], result.generic ? { queryParams: result.queryParams } : undefined)
        })
    } else {
      // direct navigation for normal routes
      this.router.navigateByUrl(favorite.route)
    }
  }

  getResolvedUrl$(favorite: Favorite): Observable<string> {
    if (!favorite || !favorite.route) {
      return of('')
    }

    if (favorite.route.startsWith('/entity-link/') && favorite.dtoType) {
      const entityType = favorite.dtoType

      return this.entityLinkService
        .getEntityLinkData$({
          type: entityType
        })
        .pipe(
          take(1),
          map((result) => {
            // build the route with query params if needed
            let route = result.url
            if (result.generic && result.queryParams) {
              const params = new URLSearchParams(result.queryParams).toString()
              route = `${result.url}?${params}`
            }

            // return ONLY the route path - no origin, no hash
            return route
          })
        )
    } else {
      // direct route - return as is (should be just the path)
      return of(favorite.route)
    }
  }
}
